import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { BatteryCharging, FileText, LucideAngularModule } from 'lucide-angular';
import { BessContractSupportRow } from '../../../core/models/domain.models';
import { ContractsService } from '../../contract/services/contracts.service';
import { ToastService } from '../../../shared/ui/toast.service';

@Component({
  standalone: true,
  selector: 'app-bess-contract-support-page',
  imports: [CommonModule, LucideAngularModule],
  template: `
    <div class="support-shell p-2 p-lg-2">
      <section class="hero-card">
        <div>
          <div class="eyebrow">Bess</div>
          <h1>BESS Contract Support</h1>
          <p>Track contracts where BESS participates as buyer for charging or seller for discharging in bilateral transactions.</p>
          <button class="create-btn" type="button" (click)="router.navigateByUrl('/contracts/new?entityType=bess')">
            Create BESS Contract
          </button>
        </div>
        <div class="hero-icons">
          <span class="hero-icon accent"><lucide-icon [img]="BatteryChargingIcon" [size]="24"></lucide-icon></span>
          <span class="hero-icon"><lucide-icon [img]="FileTextIcon" [size]="24"></lucide-icon></span>
        </div>
      </section>

      <section class="summary-grid">
        <article class="summary-card">
          <small>Total BESS Contracts</small>
          <strong>{{ rows().length }}</strong>
        </article>
        <article class="summary-card">
          <small>BESS As Buyer</small>
          <strong>{{ buyerCount() }}</strong>
        </article>
        <article class="summary-card">
          <small>BESS As Seller</small>
          <strong>{{ sellerCount() }}</strong>
        </article>
        <article class="summary-card">
          <small>Unique BESS Assets</small>
          <strong>{{ uniqueBessCount() }}</strong>
        </article>
      </section>

      <section class="table-card">
        <div class="section-head">
          <h2>Contract Participation</h2>
          <span>Generator -> BESS for charging, BESS -> Buyer for discharging</span>
        </div>

        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Contract No.</th>
                <th>BESS Asset</th>
                <th>BESS Role</th>
                <th>Counterparty</th>
                <th>Energy Flow</th>
                <th>Quantum</th>
                <th>Agreement</th>
                <th>Period</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              @if (loading()) {
                <tr><td colspan="9" class="empty-state">Loading BESS contract support data...</td></tr>
              } @else if (rows().length === 0) {
                <tr><td colspan="9" class="empty-state">No BESS-linked contracts found yet.</td></tr>
              } @else {
                @for (row of rows(); track row.contract_id + '-' + row.bess_role + '-' + row.bess_vendor_id) {
                  <tr>
                    <td>{{ row.contract_number }}</td>
                    <td>{{ row.bess_vendor_name }}</td>
                    <td><span class="pill" [class.seller]="row.bess_role === 'Seller'">{{ row.bess_role }}</span></td>
                    <td>{{ row.counterparty_vendor_name }}</td>
                    <td>{{ row.energy_flow }}</td>
                    <td>{{ row.quantum || '-' }}</td>
                    <td>{{ row.agreement_type || '-' }}</td>
                    <td>{{ row.period_from }} to {{ row.period_to }}</td>
                    <td><span class="status">{{ row.status }}</span></td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>
      </section>
    </div>
  `,
  styles: [
    `
      .support-shell { display: grid; gap: 1rem; }
      .hero-card, .summary-card, .table-card {
        border: 1px solid rgba(18, 40, 72, 0.08);
        border-radius: 1rem;
        background: linear-gradient(180deg, #ffffff 0%, #f7fafc 100%);
        box-shadow: 0 16px 35px rgba(15, 23, 42, 0.06);
      }
      .hero-card { display:flex; justify-content:space-between; align-items:center; gap:1rem; padding:1.5rem; }
      .eyebrow { text-transform:uppercase; letter-spacing:.08em; font-size:.72rem; color:#6b7b8c; margin-bottom:.35rem; }
      .hero-card h1, .section-head h2 { margin:0; }
      .hero-card p, .section-head span, .summary-card small, .empty-state { color:#607286; }
      .create-btn {
        margin-top: 1rem;
        border: 0;
        border-radius: .8rem;
        background: linear-gradient(135deg, #0f5ccf, #2563eb);
        color: #fff;
        font-weight: 700;
        padding: .8rem 1.15rem;
      }
      .hero-icons { display:flex; gap:.75rem; }
      .hero-icon { width:4rem; height:4rem; display:grid; place-items:center; border-radius:1rem; background:linear-gradient(135deg, #0f766e, #0f4c81); color:#fff; }
      .hero-icon.accent { background:linear-gradient(135deg, #0f5ccf, #3b82f6); }
      .summary-grid { display:grid; grid-template-columns:repeat(4, minmax(0, 1fr)); gap:1rem; }
      .summary-card { padding:1rem 1.1rem; }
      .summary-card small { display:block; text-transform:uppercase; letter-spacing:.05em; font-size:.74rem; }
      .summary-card strong { font-size:1.65rem; color:#18324d; }
      .table-card { padding:1.25rem; }
      .section-head { display:flex; justify-content:space-between; align-items:center; gap:1rem; margin-bottom:1rem; }
      .table-wrap { overflow:auto; }
      table { width:100%; border-collapse:collapse; }
      th, td { padding:.85rem .75rem; border-bottom:1px solid rgba(18,40,72,.08); text-align:left; white-space:nowrap; }
      th { color:#6b7b8c; font-size:.8rem; text-transform:uppercase; letter-spacing:.05em; }
      .pill, .status { display:inline-flex; align-items:center; padding:.3rem .65rem; border-radius:999px; font-size:.8rem; font-weight:700; }
      .pill { background:#dbeafe; color:#1d4ed8; }
      .pill.seller { background:#dcfce7; color:#166534; }
      .status { background:#eef2f7; color:#44556a; text-transform:capitalize; }
      .empty-state { text-align:center; padding:2rem .5rem; }
      @media (max-width: 960px) {
        .hero-card, .section-head { flex-direction:column; align-items:flex-start; }
        .summary-grid { grid-template-columns:repeat(2, minmax(0, 1fr)); }
      }
    `,
  ],
})
export class BessContractSupportPage {
  protected readonly router = inject(Router);
  private readonly contractsService = inject(ContractsService);
  private readonly toast = inject(ToastService);

  protected readonly BatteryChargingIcon = BatteryCharging;
  protected readonly FileTextIcon = FileText;
  protected readonly loading = signal(false);
  protected readonly rows = signal<BessContractSupportRow[]>([]);
  protected readonly buyerCount = computed(() => this.rows().filter((item) => item.bess_role === 'Buyer').length);
  protected readonly sellerCount = computed(() => this.rows().filter((item) => item.bess_role === 'Seller').length);
  protected readonly uniqueBessCount = computed(() => new Set(this.rows().map((item) => item.bess_vendor_id)).size);

  constructor() {
    this.load();
  }

  private load(): void {
    this.loading.set(true);
    this.contractsService.listBessSupport().subscribe({
      next: (rows) => {
        this.rows.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.loading.set(false);
        this.toast.show('Failed to load BESS contract support data', '', 'danger');
      },
    });
  }
}
